import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { History as EventsIcon, Group as GroupIcon } from '@mui/icons-material';
import { useDeviceSoftwareUpdate } from '@/hooks/use-software-update-rollouts';
import { useSoftwareUpdatePackageDefinitions } from '@/hooks/use-software-update-packages';
import type {
  DeviceSoftwareUpdateEvent,
  DeviceSoftwareUpdateGroup,
} from '@/lib/software-update-rollout.service';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { TextLink } from '@/components/ui/text-link';
import {
  Dash,
  EventChip,
  EventFromVersionCell,
  EventTimeCell,
  EventToVersionCell,
  EVENT_ROW_SX,
  ReasonChip,
} from '@/components/software-update/event-cells';
import {
  DEVICE_SOFTWARE_EVENT_TYPES,
  getDeviceUpdateEventVisual,
} from '@/lib/software-update-config';
import { packageKeyOf } from '@/components/software-update/package-helpers';
import { typography as t, radius } from '@/theme/tokens';

const ALL = '__all__';
const EVENT_PAGE_LIMIT = 15;
const EVENT_REFRESH_INTERVAL_MS = 10000;

const ICON_SX = { color: '#666666', fontSize: 20 };

interface DeviceSoftwareUpdateTabProps {
  deviceId: string;
}

export function DeviceSoftwareUpdateTab({ deviceId }: DeviceSoftwareUpdateTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rolloutFilter, setRolloutFilter] = useState<string>(
    searchParams.get('rollout_id') ?? '',
  );
  const [eventTypeFilter, setEventTypeFilter] = useState<string>(ALL);
  const [packageFilter, setPackageFilter] = useState<string>(ALL);
  const [includeProgress, setIncludeProgress] = useState(false);
  const [snapshotBefore, setSnapshotBefore] = useState(() => new Date().toISOString());
  const [page, setPage] = useState(0);
  const { data: packageDefinitions } = useSoftwareUpdatePackageDefinitions();
  const selectedPackage = (packageDefinitions ?? []).find(
    (pkg) => packageKeyOf(pkg) === packageFilter,
  );

  useEffect(() => {
    setPage(0);
  }, [rolloutFilter, eventTypeFilter, packageFilter, includeProgress]);

  const params = useMemo(
    () => ({
      rollout_id: rolloutFilter || undefined,
      event_type: eventTypeFilter === ALL ? undefined : eventTypeFilter,
      package: selectedPackage
        ? { type: selectedPackage.type, package_name: selectedPackage.package_name }
        : undefined,
      before: snapshotBefore,
      exclude_progress: !includeProgress,
      limit: EVENT_PAGE_LIMIT,
      offset: page * EVENT_PAGE_LIMIT,
    }),
    [rolloutFilter, eventTypeFilter, selectedPackage, includeProgress, snapshotBefore, page],
  );
  const { data, isLoading } = useDeviceSoftwareUpdate(deviceId, params, {
    refetchInterval: EVENT_REFRESH_INTERVAL_MS,
  });

  const handleRolloutChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setRolloutFilter(value);
    const next = new URLSearchParams(searchParams);
    if (value) next.set('rollout_id', value);
    else next.delete('rollout_id');
    setSearchParams(next, { replace: true });
  };

  const refreshSnapshot = () => {
    setSnapshotBefore(new Date().toISOString());
    setPage(0);
  };

  const events = data?.events?.events ?? [];
  const totalEvents = data?.events?.total ?? 0;
  const newerCount = data?.events?.newer_count ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {!isLoading && <GroupEyebrow group={data?.group ?? null} />}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventsIcon sx={ICON_SX} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Events
                </Typography>
              </Box>
            }
            sx={{ pb: 0 }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Select
                size="small"
                value={rolloutFilter}
                onChange={handleRolloutChange}
                displayEmpty
                sx={{ minWidth: 260, fontSize: 12 }}
              >
                <MenuItem value="">All rollouts</MenuItem>
                {(data?.rollouts ?? []).map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name || r.id}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value)}
                sx={{ minWidth: 180, fontSize: 12 }}
              >
                <MenuItem value={ALL}>All events</MenuItem>
                {DEVICE_SOFTWARE_EVENT_TYPES.map((et) => (
                  <MenuItem key={et} value={et}>
                    {et}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={packageFilter}
                onChange={(e) => setPackageFilter(e.target.value)}
                sx={{ minWidth: 220, fontSize: 12 }}
              >
                <MenuItem value={ALL}>All packages</MenuItem>
                {(packageDefinitions ?? []).map((pkg) => (
                  <MenuItem key={packageKeyOf(pkg)} value={packageKeyOf(pkg)}>
                    {pkg.display_name}
                  </MenuItem>
                ))}
              </Select>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeProgress}
                    onChange={(e) => setIncludeProgress(e.target.checked)}
                  />
                }
                label="Include progress events"
                slotProps={{ typography: { fontSize: 12 } }}
              />
            </Box>
            {newerCount > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Chip
                  label={`${newerCount.toLocaleString()} new event${newerCount === 1 ? '' : 's'} — refresh`}
                  size="small"
                  clickable
                  onClick={refreshSnapshot}
                  sx={{
                    height: 24,
                    fontSize: 12,
                    fontWeight: t.fontWeight.medium,
                    borderRadius: `${radius.sm}px`,
                    bgcolor: '#EFF4FF',
                    color: '#1D4ED8',
                  }}
                />
              </Box>
            )}
            <EventsTable events={events} loading={isLoading} />
            {totalEvents > EVENT_PAGE_LIMIT && (
              <TablePagination
                component="div"
                count={totalEvents}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={EVENT_PAGE_LIMIT}
                rowsPerPageOptions={[EVENT_PAGE_LIMIT]}
                sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
    </Box>
  );
}

// Which software-update group this device belongs to (at most one). Rendered as
// a boxless "eyebrow" heading line above the Events card — a single fact doesn't
// justify a card, so it sits on the page background as section context. Shows an
// em dash when the device is ungrouped.
function GroupEyebrow({
  group,
}: {
  group: DeviceSoftwareUpdateGroup | null;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
      <GroupIcon sx={{ color: group ? '#666666' : '#9E9E9E', fontSize: 20 }} />
      <Typography
        variant="caption"
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontSize: 10.5,
          color: 'text.secondary',
        }}
      >
        Group
      </Typography>
      {group ? (
        <TextLink
          to={`/software-update/groups/${group.id}`}
          sx={{ fontSize: 13 }}
        >
          {group.name}
        </TextLink>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          —
        </Typography>
      )}
    </Box>
  );
}

function EventsTable({
  events,
  loading,
}: {
  events: DeviceSoftwareUpdateEvent[];
  loading: boolean;
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 176 }}>Client time</TableCell>
            <TableCell sx={{ width: 220 }}>Rollout</TableCell>
            <TableCell>Package</TableCell>
            <TableCell sx={{ width: 160 }}>From version</TableCell>
            <TableCell sx={{ width: 160 }}>To version</TableCell>
            <TableCell sx={{ width: 160 }}>Event</TableCell>
            <TableCell sx={{ width: 160 }}>Reason</TableCell>
            <TableCell sx={{ width: 120 }}>Progress (%)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableSkeleton rows={6} columns={8} columnWidths={[160, 220, 200, 160, 160, 160, 160, 120]} />
          ) : events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No events match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            events.map((ev) => <EventRow key={ev.id} event={ev} />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EventRow({ event }: { event: DeviceSoftwareUpdateEvent }) {
  const packageLabel = event.package_display_name || event.package_name;
  const rolloutLabel = event.rollout_name || event.rollout_id;
  const showProgress =
    event.event_type === 'download_progress' &&
    event.progress_percent !== null &&
    event.progress_percent !== undefined;
  return (
    <TableRow sx={EVENT_ROW_SX}>
      <EventTimeCell value={event.client_created_at} />
      <TableCell>
        {event.rollout_id ? (
          <TextLink to={`/software-update/rollouts/${event.rollout_id}`} sx={{ fontSize: 13 }}>
            {rolloutLabel}
          </TextLink>
        ) : (
          <Dash />
        )}
      </TableCell>
      <TableCell>
        {packageLabel ? (
          <Typography variant="body2">{packageLabel}</Typography>
        ) : (
          <Dash />
        )}
      </TableCell>
      <EventFromVersionCell fromVersion={event.from_version} />
      <EventToVersionCell targetVersion={event.package_version} />
      <TableCell>
        <EventChip label={event.event_type} {...getDeviceUpdateEventVisual(event.event_type)} />
      </TableCell>
      <TableCell>
        {event.reason ? (
          <ReasonChip label={event.reason} />
        ) : (
          <Dash />
        )}
      </TableCell>
      <TableCell>
        {showProgress ? (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {event.progress_percent}
          </Typography>
        ) : (
          <Dash />
        )}
      </TableCell>
    </TableRow>
  );
}
